import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { useTopBarContent } from '../../client/contexts/TopBarContentContext';
import {
  useQuery,
  getLocationItems,
  getAllLocations,
  bulkDeleteLocationMembers,
  bulkDeleteLocationContent,
  bulkDeleteLocationEvents,
  moveLocationMembers,
  moveLocationContent,
  moveLocationEvents,
  setMemberPostWithoutApproval,
} from 'wasp/client/operations';
import LocationContentApprovalSettingsCard from './LocationContentApprovalSettingsCard';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Trash2, ArrowRightLeft, Check, Users, FileText, Calendar, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import LoadingSpinner from '../../admin/layout/LoadingSpinner';
import { TableFilterLayout } from '../../components/ui/table-filter-layout';
import { toast } from '../../hooks/use-toast';
import useDebounce from '../../client/hooks/useDebounce';

// Matches the dashboard's "clean style" LocationDropdown:
// text-base font-semibold text-content-text, no border/icon/chevron, custom dropdown list
function LocationSwitcherButton({ currentId, locations, onSelect }: {
  currentId: string | undefined;
  locations: { id: string; name: string }[];
  onSelect: (id: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentName = locations.find(l => l.id === currentId)?.name ?? 'Select Location';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-2 text-base font-semibold text-content-text hover:text-content-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded px-2 py-1 transition-colors duration-200"
      >
        {currentName}
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-80 overflow-y-auto">
          {locations.map(l => (
            <button
              key={l.id}
              onClick={() => { onSelect(l.id); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-sidebar-hover transition-colors first:rounded-t-lg last:rounded-b-lg"
            >
              <div className="flex-1 text-base font-medium text-content-text truncate">{l.name}</div>
              {l.id === currentId && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type Tab = 'members' | 'content' | 'events';

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'members', label: 'Members', icon: Users },
  { key: 'content', label: 'Content', icon: FileText },
  { key: 'events', label: 'Events', icon: Calendar },
];

// All column spans must sum to 12 (platform standard)
// Checkbox is embedded inside the first data column — no separate column needed
const MEMBERS_COLUMNS = [
  { title: 'Name / Email', span: 4 },
  { title: 'Role', span: 2 },
  { title: 'Access', span: 2 },
  { title: 'Verified', span: 1 },
  { title: 'Auto-publish', span: 1 },
  { title: 'Joined', span: 1 },
  { title: 'Actions', span: 1 },
];

const CONTENT_COLUMNS = [
  { title: 'Title', span: 5 },
  { title: 'Type', span: 2 },
  { title: 'Category', span: 2 },
  { title: 'Visibility', span: 1 },
  { title: 'Created', span: 1 },
  { title: 'Actions', span: 1 },
];

const EVENTS_COLUMNS = [
  { title: 'Title', span: 5 },
  { title: 'Start', span: 2 },
  { title: 'Status', span: 2 },
  { title: 'Attendees', span: 1 },
  { title: 'Created', span: 1 },
  { title: 'Actions', span: 1 },
];

function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const AdminLocationItemsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { setTopBarContent, clearTopBarContent } = useTopBarContent();

  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveTargetId, setMoveTargetId] = useState('');

  const search = useDebounce(searchInput, 300);

  const { data: itemsData, isLoading, refetch } = useQuery(getLocationItems, {
    locationId: id!,
    tab: activeTab,
    page: currentPage,
    limit: 20,
    search
  });

  const { data: allLocationsData } = useQuery(getAllLocations, { page: 1, limit: 200 });

  // Inject location switcher into the global top bar while this page is mounted.
  useEffect(() => {
    const locations: { id: string; name: string }[] = (allLocationsData as any)?.locations ?? [];
    setTopBarContent(
      <LocationSwitcherButton
        currentId={id}
        locations={locations}
        onSelect={(newId) => navigate(`/admin/taxonomy/location-names/${newId}/items`)}
      />
    );
    return () => clearTopBarContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, allLocationsData, navigate, setTopBarContent, clearTopBarContent]);

  if (!user || !isAdministrator(user)) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  const items: any[] = (itemsData as any)?.items ?? [];
  const counts: Record<Tab, number> = (itemsData as any)?.counts ?? { members: 0, content: 0, events: 0 };
  const totalPages: number = (itemsData as any)?.totalPages ?? 1;

  const otherLocations = ((allLocationsData as any)?.locations ?? []).filter((l: any) => l.id !== id);

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((item: any) => item.id)));
  };

  const toggleOne = (itemId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    setCurrentPage(1);
    setSearchInput('');
    setMoveTargetId('');
  };

  const handleBulkDelete = async (idsToDelete: string[]) => {
    if (!idsToDelete.length) return;
    if (!confirm(`Delete ${idsToDelete.length} item(s)? This cannot be undone.`)) return;
    try {
      if (activeTab === 'members') await bulkDeleteLocationMembers({ locationId: id!, membershipIds: idsToDelete });
      else if (activeTab === 'content') await bulkDeleteLocationContent({ locationId: id!, contentIds: idsToDelete });
      else await bulkDeleteLocationEvents({ locationId: id!, eventIds: idsToDelete });
      setSelectedIds(new Set());
      refetch();
      toast({ title: 'Deleted', description: `${idsToDelete.length} item(s) deleted.` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleMove = async (idsToMove: string[], toLocationId: string) => {
    if (!idsToMove.length || !toLocationId) return;
    try {
      if (activeTab === 'members') await moveLocationMembers({ fromLocationId: id!, toLocationId, membershipIds: idsToMove });
      else if (activeTab === 'content') await moveLocationContent({ fromLocationId: id!, toLocationId, contentIds: idsToMove });
      else await moveLocationEvents({ fromLocationId: id!, toLocationId, eventIds: idsToMove });
      setSelectedIds(new Set());
      setMoveTargetId('');
      refetch();
      toast({ title: 'Moved', description: `${idsToMove.length} item(s) moved.` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const currentColumns = activeTab === 'members' ? MEMBERS_COLUMNS : activeTab === 'content' ? CONTENT_COLUMNS : EVENTS_COLUMNS;

  // Filter row: first slot has checkbox + search combined, rest empty
  const filterSlots = currentColumns.map((col, idx) => {
    if (idx === 0) return {
      span: col.span,
      content: (
        <div className="flex items-center gap-2">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={e => { setSearchInput(e.target.value); setCurrentPage(1); }}
              placeholder={`Search ${activeTab}...`}
              className="w-full pl-8 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>
      )
    };
    return { span: col.span, content: <div className="h-10" /> };
  });

  return (
    <div className="w-full">

        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-20 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          {/* Title */}
          <div className="px-6 py-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Items</h1>
          </div>

          {/* Tab bar + bulk actions on the right */}
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4">
            {/* Tabs */}
            <div className="flex">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => switchTab(key)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === key
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {counts[key]}
                  </span>
                </button>
              ))}
            </div>

            {/* Bulk actions — right side of the tab bar, large screens */}
            {someSelected && (
              <div className="hidden lg:flex items-center gap-3 py-1">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                  {selectedIds.size} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkDelete(Array.from(selectedIds))}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </Button>
                <Select value={moveTargetId} onValueChange={setMoveTargetId}>
                  <SelectTrigger className="h-8 text-sm w-44">
                    <SelectValue placeholder="Move to location..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherLocations.map((l: any) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMove(Array.from(selectedIds), moveTargetId)}
                  disabled={!moveTargetId}
                >
                  <ArrowRightLeft className="w-4 h-4 mr-1" />
                  Move
                </Button>
              </div>
            )}

            {/* Bulk actions — compact for smaller screens */}
            {someSelected && (
              <div className="flex lg:hidden items-center gap-2 py-1">
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">
                  {selectedIds.size}
                </span>
                <Button variant="destructive" size="sm" onClick={() => handleBulkDelete(Array.from(selectedIds))}>
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Select value={moveTargetId} onValueChange={setMoveTargetId}>
                  <SelectTrigger className="h-8 text-sm w-32">
                    <SelectValue placeholder="Move to..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherLocations.map((l: any) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => handleMove(Array.from(selectedIds), moveTargetId)} disabled={!moveTargetId}>
                  <ArrowRightLeft className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Spacer: title row (~64px) + tab bar (~41px) = fixed height regardless of selection state */}
        <div className="h-[105px]" />

        <div className="px-6 pb-4">
          <LocationContentApprovalSettingsCard locationId={id!} />
        </div>

        {/* Column headers + search filter row */}
        <TableFilterLayout
          columns={currentColumns}
          filters={filterSlots}
          showFilters={true}
        />

        {/* Content rows */}
        <div className="w-full">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg font-medium">No {activeTab} found</p>
              <p className="text-sm mt-2">
                {search ? `No results for "${search}"` : `This location has no ${activeTab}.`}
              </p>
            </div>
          )}

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {activeTab === 'members' && items.map((item: any) => (
              <MemberRow
                key={item.id}
                item={item}
                locationId={id!}
                selected={selectedIds.has(item.id)}
                onToggle={() => toggleOne(item.id)}
                onDelete={() => handleBulkDelete([item.id])}
                onMove={(toId) => handleMove([item.id], toId)}
                otherLocations={otherLocations}
                onApprovalToggle={refetch}
              />
            ))}

            {activeTab === 'content' && items.map((item: any) => (
              <ContentRow
                key={item.id}
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={() => toggleOne(item.id)}
                onDelete={() => handleBulkDelete([item.id])}
                onMove={(toId) => handleMove([item.id], toId)}
                otherLocations={otherLocations}
              />
            ))}

            {activeTab === 'events' && items.map((item: any) => (
              <EventRow
                key={item.id}
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={() => toggleOne(item.id)}
                onDelete={() => handleBulkDelete([item.id])}
                onMove={(toId) => handleMove([item.id], toId)}
                otherLocations={otherLocations}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

// ─── Row Components ──────────────────────────────────────────────────────────

interface RowProps {
  item: any;
  selected: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onMove: (toLocationId: string) => void;
  otherLocations: any[];
  locationId?: string;
  onApprovalToggle?: () => void;
}

function RowActions({ onDelete, onMove, otherLocations }: Pick<RowProps, 'onDelete' | 'onMove' | 'otherLocations'>) {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onDelete}
        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        title="Delete"
      >
        <Trash2 className="w-4 h-4" />
      </button>
      <Select value="" onValueChange={onMove}>
        <SelectTrigger
          className="h-7 w-7 border-0 shadow-none p-0 flex items-center justify-center text-gray-400 hover:text-blue-600 focus:ring-0 focus-visible:ring-0"
          title="Move to location"
        >
          <ArrowRightLeft className="w-4 h-4" />
        </SelectTrigger>
        <SelectContent>
          {otherLocations.map((l: any) => (
            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MemberRow({
  item,
  locationId,
  selected,
  onToggle,
  onDelete,
  onMove,
  otherLocations,
  onApprovalToggle,
}: RowProps) {
  const u = item.user;
  const isStaffRole = item.role === 'ADMIN' || item.role === 'MANAGER';
  const [savingBypass, setSavingBypass] = React.useState(false);

  const handleBypassChange = async (checked: boolean) => {
    if (!locationId) return;
    setSavingBypass(true);
    try {
      await setMemberPostWithoutApproval({
        locationId,
        membershipId: item.id,
        canPostWithoutApproval: checked,
      });
      onApprovalToggle?.();
      toast({
        title: 'Updated',
        description: checked
          ? 'Member can post without approval.'
          : 'Member posts will require approval.',
      });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingBypass(false);
    }
  };

  return (
    <div className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center">
      <div className="col-span-4 flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-white">{u?.username || '—'}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{u?.email}</div>
        </div>
      </div>
      <div className="col-span-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          {item.role}
        </span>
      </div>
      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">{item.accessLevel}</div>
      <div className="col-span-1 text-sm">
        {item.isVerified
          ? <span className="text-green-600 dark:text-green-400 font-medium">Yes</span>
          : <span className="text-gray-400">No</span>}
      </div>
      <div className="col-span-1">
        {isStaffRole ? (
          <span className="text-xs text-muted-foreground">Staff</span>
        ) : (
          <Switch
            checked={Boolean(item.canPostWithoutApproval)}
            disabled={savingBypass}
            onCheckedChange={handleBypassChange}
            aria-label="Post without approval"
          />
        )}
      </div>
      <div className="col-span-1 text-xs text-gray-500 dark:text-gray-400">{formatDate(item.joinedAt)}</div>
      <div className="col-span-1">
        <RowActions onDelete={onDelete} onMove={onMove} otherLocations={otherLocations} />
      </div>
    </div>
  );
}

function ContentRow({ item, selected, onToggle, onDelete, onMove, otherLocations }: RowProps) {
  return (
    <div className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center">
      <div className="col-span-5 flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.title}</div>
      </div>
      <div className="col-span-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
          {item.type}
        </span>
      </div>
      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">{item.category || '—'}</div>
      <div className="col-span-1 text-xs text-gray-500 dark:text-gray-400">{item.visibility}</div>
      <div className="col-span-1 text-xs text-gray-500 dark:text-gray-400">{formatDate(item.createdAt)}</div>
      <div className="col-span-1">
        <RowActions onDelete={onDelete} onMove={onMove} otherLocations={otherLocations} />
      </div>
    </div>
  );
}

function EventRow({ item, selected, onToggle, onDelete, onMove, otherLocations }: RowProps) {
  return (
    <div className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center">
      <div className="col-span-5 flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.title}</div>
      </div>
      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">{formatDate(item.startTime)}</div>
      <div className="col-span-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          {item.status}
        </span>
      </div>
      <div className="col-span-1 text-sm text-gray-600 dark:text-gray-400">{item._count?.attendees ?? 0}</div>
      <div className="col-span-1 text-xs text-gray-500 dark:text-gray-400">{formatDate(item.createdAt)}</div>
      <div className="col-span-1">
        <RowActions onDelete={onDelete} onMove={onMove} otherLocations={otherLocations} />
      </div>
    </div>
  );
}

export default AdminLocationItemsPage;



